// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { TickerTagFeature } from '@shared/data-access/interfaces/api/engine/citec';
import { OptionsSelector } from '@shared/ui-kit/components/options-selector';
import { type PortfolioLoaded } from '@shared/data-access/interfaces/common/portfolio-loaded';
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppSelector } from '@advisor-app/store/store';
import { selectWatchlistPositions } from '@shared/data-access/slices/tag-manager';
import { selectCurrentPortfolio } from '@shared/data-access/slices/portfolio-positions';
import { selectAllTags } from '@shared/data-access/slices/tag-manager';
import {
  selectWorkflowAsset,
  selectWorkflowType,
} from '@advisor-app/store/slices/workflow';
import { useTranslation } from 'react-i18next';

export interface SelectedAssetsSelectorProps {
  readonly onSelectAssets: Dispatch<SetStateAction<PortfolioLoaded[]>>;
  readonly selected?: string[];
}

export const SelectedAssetsSelector: FC<SelectedAssetsSelectorProps> = ({
  onSelectAssets,
  selected,
}) => {
  const { t } = useTranslation();
  const workflow = useAppSelector(selectWorkflowType);

  const assetType = useAppSelector(selectWorkflowAsset);

  const currentPortfolio = useAppSelector(selectCurrentPortfolio);

  const selectedAssets = useAppSelector(state => selectWatchlistPositions(state));

  const assetsByType = useMemo(
    () =>
      selectedAssets.filter(({ asset_type }) =>
        workflow === 'rebalance' ? true : asset_type === assetType,
      ),
    [assetType, selectedAssets, workflow],
  );

  const tags = useAppSelector(state =>
    selectAllTags(state, TickerTagFeature.SelectedAssets),
  );

  const sourceData = useMemo(() => {
    return tags
      .flatMap(({ name, tickers }) =>
        assetsByType
          .filter(({ ticker }) => tickers.includes(ticker))
          .map(({ ticker }) => ({
            value: ticker,
            label: `${ticker} - ${name}`,
            group: name,
          })),
      )
      .concat(
        assetsByType
          .filter(
            ({ ticker }) => !tags.flatMap(tag => tag.tickers).includes(ticker),
          )
          .map(asset => ({
            value: asset.ticker,
            label: `${asset.ticker} - ${asset.name}`,
            group: t('tag-manager.non-tagged', {
              ns: 'components',
            }) as string,
          })),
      );
  }, [assetsByType, t, tags]);

  const [optionSelected, setOptionSelected] = useState([] as string[]);

  const tickers = useMemo(
    () => selected ?? optionSelected,
    [optionSelected, selected],
  );

  const onChangeValueSelected = useCallback(
    (values: string[]) => {
      setOptionSelected(values);

      const addedValues = values.filter(value => !optionSelected.includes(value));

      const removedValues = optionSelected.filter(value => !values.includes(value));

      if (addedValues.length > 0) {
        return onSelectAssets(prev =>
          prev.concat(
            addedValues.map(
              ticker =>
                ({
                  ticker,
                  outstanding_balance: 0,
                  unrealized_capital_gains: 0,
                  date: new Date(Date.now()).toISOString().split('T')[0] as string,
                  selectedAsset: true,
                } as PortfolioLoaded),
            ),
          ),
        );
      }

      if (removedValues.length > 0) {
        return onSelectAssets(prev =>
          prev.filter(value => !removedValues.includes(value.ticker)),
        );
      }
    },
    [onSelectAssets, optionSelected],
  );

  useEffect(() => {
    if (currentPortfolio.length === 0 && tickers.length > 0) {
      /* istanbul ignore next */
      setOptionSelected([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPortfolio.length]);

  return (
    <OptionsSelector
      defaultText={t('tag-manager.add-assets', { ns: 'components' }) as string}
      multiple
      checkbox
      optionsAvailable={sourceData}
      onChangeValueSelected={onChangeValueSelected}
      optionSelected={tickers}
      selectType="white"
      grouping
      dataTestId="selected-assets-selector"
      selectedAll={false}
    />
  );
};
